// Single source of truth for analytics stage metadata.
// Consumed by both AnalyticsDashboard (heatmap rows) and AnalyticsReport (summary cards).
//
// Stage keys mirror backend event_type values emitted by:
//   - handler_analysis_element.go   (base_image_prompt / state_image_prompt / image_gen / ref_image / asset)
//   - handler_shot.go               (prompt / video_gen / video_ref_match)
//   - handler_episode.go            (prompt)
//   - handler_project.go            (asset)
//   - handler_search.go             (search / ref_image)
//   - handler_analysis.go           (analysis)
// Plus `state_image` which is kept in the aggregator's stage map for historical compatibility.

import i18n from 'i18next';

export interface StageInfo {
  name: string;
  detail: string;
  step: string;
}

const STAGE_KEYS = [
  'base_image_prompt',
  'state_image_prompt',
  'video_ref_match',
  'prompt',
  'asset',
  'ref_image',
  'state_image',
  'image_gen',
  'video_gen',
  'search',
  'analysis',
  'asset_episodes',
] as const;

// UX display order for the Dashboard heatmap:
// new subtype rows first (above the fold), then legacy aggregate rows.
export const stageOrder: string[] = [
  'base_image_prompt',
  'state_image_prompt',
  'prompt',
  'asset',
  'ref_image',
  'video_ref_match',
  'state_image',
  'image_gen',
  'video_gen',
  'search',
  'analysis',
  'asset_episodes',
];

export function getStageInfo(key: string): StageInfo {
  const t = (k: string) => i18n.t(k, { ns: 'admin' });
  if (STAGE_KEYS.includes(key as any)) {
    return {
      name: t(`analyticsStages.${key}.name`),
      detail: t(`analyticsStages.${key}.detail`),
      step: t(`analyticsStages.${key}.step`),
    };
  }
  return { name: key, detail: '', step: '' };
}

// For backward compatibility: generate the stageInfo record lazily
export const stageInfo: Record<string, StageInfo> = new Proxy(
  {} as Record<string, StageInfo>,
  {
    get(_target, prop: string) {
      return getStageInfo(prop);
    },
    ownKeys() {
      return [...STAGE_KEYS];
    },
    getOwnPropertyDescriptor(_target, prop: string) {
      if (STAGE_KEYS.includes(prop as any)) {
        return { configurable: true, enumerable: true, writable: false, value: getStageInfo(prop) };
      }
      return undefined;
    },
  }
);
